// Auto-density for the Zeitleiste (timeline) view. There is NO manual Tag/Woche/
// Monat toggle: every row is its own track (markers never collide vertically), so
// the zoom level only changed how spread-out the dots were, not what you could
// read. Instead the scale is derived from the data span — day columns for a short
// span (so individual days are legible), week (KW) columns for the usual helpdesk
// span, collapsing to month columns only when a genuinely long span (e.g. a
// months-old still-open ticket) would otherwise make the header a wall of 50+ KW
// ticks. dayWidth FILLS the available canvas (like the calendar's 1fr grid, so the
// board reflows live when the detail split is resized); a per-scale minimum just
// stops columns collapsing — a dense/long span scrolls horizontally instead.

export type TimelineScale = "day" | "week" | "month";

export interface TimelineDensity {
  scale: TimelineScale;
  dayWidth: number;
}

// ≤ ~3.5 weeks → day columns (a day number stays legible at the day min width).
// Beyond ~17 weeks the KW header gets unwieldy → month columns.
const DAY_MAX_DAYS = 24;
const WEEK_MAX_DAYS = 120;
// Minimum column width per scale — below this the span scrolls instead of
// squishing. There is deliberately NO maximum: columns stretch to fill the pane.
const MIN_WIDTH: Record<TimelineScale, number> = { day: 22, week: 16, month: 4 };

// The left (title) column of the Zeitleiste is user-resizable and persisted; the
// width is clamped so it can't be dragged off-screen or too narrow to read.
export const TIMELINE_TITLE_MIN = 180;
export const TIMELINE_TITLE_MAX = 560;
export const TIMELINE_TITLE_DEFAULT = 280;

export function clampTitleWidth(px: number): number {
  if (!Number.isFinite(px)) return TIMELINE_TITLE_DEFAULT;
  return Math.min(TIMELINE_TITLE_MAX, Math.max(TIMELINE_TITLE_MIN, Math.round(px)));
}

export function timelineDensity(totalDays: number, available: number): TimelineDensity {
  const scale: TimelineScale =
    totalDays > WEEK_MAX_DAYS ? "month" : totalDays > DAY_MAX_DAYS ? "week" : "day";
  const fit = totalDays > 0 && available > 0 ? available / totalDays : 0;
  return { scale, dayWidth: Math.max(MIN_WIDTH[scale], fit) };
}

// The start/end a row's bar spans on the timeline. An explicit Zeitraum (timeline)
// cell is a real A→B range and wins; otherwise ticket boards span creation→
// resolved — and an OPEN ticket (no resolved date) is still running, so its bar
// stretches creation→today (the elapsed open-since duration). Project/personal
// boards pin on the single due date. Returns a null start for a row with no date
// at all (not plotted).
export interface BarDatesInput {
  timeline: { start: string; end: string } | null;
  createdAt: string | null;
  resolvedAt: string | null;
  due: string | null;
}

export function barDates(
  r: BarDatesInput,
  ticketMode: boolean,
  todayIso: string,
): { startIso: string | null; endIso: string | null } {
  if (r.timeline && r.timeline.start && r.timeline.end) {
    const { start, end } = r.timeline;
    // A reversed range (an import can store start > end) is normalized, not
    // collapsed to a milestone, so the span stays visible.
    return start <= end ? { startIso: start, endIso: end } : { startIso: end, endIso: start };
  }
  if (ticketMode) {
    // No creation date ⇒ no bar at all (don't widen the axis to a phantom).
    if (!r.createdAt) return { startIso: null, endIso: null };
    // Open ticket → today; resolved → resolved date.
    return { startIso: r.createdAt, endIso: r.resolvedAt ?? todayIso };
  }
  return { startIso: r.due, endIso: r.due };
}
